"use client";

import { useTranslations } from "next-intl";
import {
  Text,
  Card,
  Group,
  Button,
  Badge,
  Stack,
  TbChevronLeft,
  TbChevronRight,
} from "@forestsync/ui";
import { TOKEN_STATUS_TW_CLASS } from "@forestsync/lib/type-utils";
import { TOKEN_STATUS, TOKEN_STATUS_NAMES } from "@forestsync/db";
import { getTokenStatusDisplayName } from "@forestsync/lib/type-utils";

interface TokenControlsProps {
  // Pagination props
  currentPage: number;
  totalPages: number;
  totalCount: number;
  displayPage: number;
  isPaginationLoading: boolean;
  onPageChange: (page: number) => void;
}

export function TokenControls({
  currentPage,
  totalPages,
  totalCount,
  displayPage,
  isPaginationLoading,
  onPageChange,
}: TokenControlsProps) {
  const t = useTranslations();

  const legend = [
    {
      label: t("common.statusLabels.active"),
      status: TOKEN_STATUS.ACTIVE,
    },
    {
      label: t("common.statusLabels.spent"),
      status: TOKEN_STATUS.RETIRED,
    },
    {
      label: t("common.statusLabels.destroyed"),
      status: TOKEN_STATUS.REVERSED,
    },
    {
      label: t("common.statusLabels.insurance_pool"),
      status: TOKEN_STATUS.INSURANCE_POOL,
    },
  ];

  return (
    <Stack gap="md">
      {/* Status Legend */}
      <Group gap="sm" wrap="wrap">
        {legend.map((l) => {
          const twClass = TOKEN_STATUS_TW_CLASS[l.status];
          return (
            <span
              key={l.label}
              className={`px-3 py-1 text-xs font-medium rounded-full ${twClass}`}
            >
              {getTokenStatusDisplayName(l.status, t)}
            </span>
          );
        })}
      </Group>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Card withBorder padding="xs" className="bg-gray-50 dark:bg-gray-800">
          <Group justify="space-between" align="center">
            <Text size="sm" className="text-gray-600 dark:text-gray-400">
              {t("sink.showingTokensPage", {
                current: displayPage,
                total: totalPages,
                count: totalCount, // Show current page count, not total
              })}
            </Text>

            <Group gap="sm">
              <Button
                variant="light"
                size="compact-md"
                disabled={currentPage === 1 || isPaginationLoading}
                onClick={() => onPageChange(currentPage - 1)}
                loading={isPaginationLoading}
                leftSection={
                  isPaginationLoading ? undefined : <TbChevronLeft size={16} />
                }
                className="hidden sm:flex"
              >
                {t("common.previous")}
              </Button>

              {/* Mobile icon-only previous button */}
              <Button
                variant="light"
                size="compact-md"
                disabled={currentPage === 1 || isPaginationLoading}
                onClick={() => onPageChange(currentPage - 1)}
                loading={isPaginationLoading}
                className="sm:hidden"
              >
                <TbChevronLeft size={16} />
              </Button>

              <Badge
                variant="light"
                size="sm"
                className="min-w-[80px] justify-center"
              >
                {displayPage} / {totalPages}
              </Badge>

              <Button
                variant="light"
                size="compact-md"
                disabled={currentPage === totalPages || isPaginationLoading}
                onClick={() => onPageChange(currentPage + 1)}
                loading={isPaginationLoading}
                rightSection={
                  isPaginationLoading ? undefined : <TbChevronRight size={16} />
                }
                className="hidden sm:flex"
              >
                {t("common.next")}
              </Button>

              {/* Mobile icon-only next button */}
              <Button
                variant="light"
                size="compact-md"
                disabled={currentPage === totalPages || isPaginationLoading}
                onClick={() => onPageChange(currentPage + 1)}
                loading={isPaginationLoading}
                className="sm:hidden"
              >
                <TbChevronRight size={16} />
              </Button>
            </Group>
          </Group>
        </Card>
      )}
    </Stack>
  );
}
